// Daily site log (V0.4.15): kept light — quick answers, photos and works done per active building, and a voice note SWI
// AI turns into the summary. Pure helpers shared by the page and the function, tested in test/daily-check.mts.
import { norm, similar } from './weekly';

export type Weather = 'fine' | 'cloud' | 'showers' | 'heavy_rain';
export const WEATHER: { value: Weather; label: string }[] = [
  { value: 'fine', label: 'Fine' },
  { value: 'cloud', label: 'Cloud' },
  { value: 'showers', label: 'Showers' },
  { value: 'heavy_rain', label: 'Heavy rain' },
];
export const ACTIVE_STAGES = new Set(['mobilising', 'construction']);

export type DailyLog = {
  id: string;
  site_id: string;
  log_date: string;
  weather: Weather | null;
  weather_stop_h: number | null;
  barge: boolean | null;
  barge_note: string | null;
  transfers: number | null;
  visitor_org_ids: string[];
  onsite_org_ids: string[];
  hold_up: boolean | null;
  incident: boolean | null;
  instructions: boolean | null;
  summary: string | null;
  ai_fields: string[];
  voice: { file_id: string | null; seconds: number; transcript: string; at: string }[];
  status: 'draft' | 'signed';
  signed_at: string | null;
  signed_name: string | null;
  updated_at: string;
};
export type Work = { text: string; done: boolean; from?: 'lookahead' | 'own' };
export type DayBuilding = {
  id: string;
  log_id: string;
  building_id: string;
  no_work: boolean;
  works: Work[];
  summary: string | null;
  hold_up: string | null;
  eot_evidence: boolean;
};

/** Fiji calendar day (UTC+12, no daylight saving). */
export const fijiToday = (now = new Date()) => new Date(now.getTime() + 12 * 3600 * 1000).toISOString().slice(0, 10);
export const addDays = (d: string, n: number) => new Date(Date.parse(d + 'T00:00:00Z') + n * 86400000).toISOString().slice(0, 10);
export const dayLabel = (d: string) => new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

/** The tick list for one building: last week's look-ahead plus anything already ticked or added on the day. */
export function worksList(lookahead: string[], saved: Work[]): Work[] {
  const out: Work[] = saved.map((w) => ({ ...w }));
  for (const t of lookahead) if (!out.some((w) => norm(w.text) === norm(t))) out.push({ text: t, done: false, from: 'lookahead' });
  return out;
}

// ---------- voice note → answers ----------
export type VoiceProposal = {
  weather: Weather | null;
  weather_stop_h: number | null;
  barge: boolean | null;
  barge_note: string | null;
  transfers: number | null;
  visitors: string[]; // organisation names as said
  onsite: string[];
  hold_up: boolean | null;
  incident: boolean | null;
  instructions: boolean | null;
  summary: string | null;
  buildings: { name: string; no_work: boolean | null; summary: string | null; works_done: string[]; hold_up: string | null }[];
  quotes: Record<string, string>; // field → the words it came from
};
const W = new Set(['fine', 'cloud', 'showers', 'heavy_rain']);
const s = (v: unknown, max = 1200) => (typeof v === 'string' && v.trim() ? v.replace(/\s+/g, ' ').trim().slice(0, max) : null);
const b = (v: unknown) => (typeof v === 'boolean' ? v : null);
const n = (v: unknown, lo: number, hi: number) => (typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi ? Math.round(v * 10) / 10 : null);

export function cleanProposal(raw: Record<string, unknown> | null | undefined): VoiceProposal {
  const r = raw ?? {};
  const list = (v: unknown, max = 20) => (Array.isArray(v) ? v.map((x) => s(x, 120)).filter(Boolean).slice(0, max) as string[] : []);
  const quotes: Record<string, string> = {};
  if (r.quotes && typeof r.quotes === 'object') for (const [k, v] of Object.entries(r.quotes as Record<string, unknown>)) { const q = s(v, 300); if (q) quotes[k.slice(0, 60)] = q; }
  return {
    weather: W.has(String(r.weather)) ? (r.weather as Weather) : null,
    weather_stop_h: n(r.weather_stop_h, 0, 24),
    barge: b(r.barge),
    barge_note: s(r.barge_note, 300),
    transfers: (() => { const x = n(r.transfers, 0, 200); return x === null ? null : Math.round(x); })(),
    visitors: list(r.visitors),
    onsite: list(r.onsite),
    hold_up: b(r.hold_up),
    incident: b(r.incident),
    instructions: b(r.instructions),
    summary: s(r.summary, 2000),
    buildings: (Array.isArray(r.buildings) ? r.buildings : [])
      .filter((x): x is Record<string, unknown> => Boolean(x) && typeof x === 'object')
      .slice(0, 20)
      .map((x) => ({ name: s(x.name, 80) ?? '', no_work: b(x.no_work), summary: s(x.summary, 1200), works_done: list(x.works_done, 40), hold_up: s(x.hold_up, 300) }))
      .filter((x) => x.name),
    quotes,
  };
}

/** Organisation names said in the voice note → organisation ids (case-insensitive, short names count). */
export function matchOrgs(names: string[], orgs: { id: string; name: string; short_code?: string | null }[]): string[] {
  const out: string[] = [];
  for (const nm of names) {
    const k = norm(nm);
    const hit =
      orgs.find((o) => norm(o.name) === k || (o.short_code && norm(o.short_code) === k)) ??
      orgs.find((o) => norm(o.name).split(' ').includes(k) || norm(o.name).startsWith(k + ' ')) ??
      orgs.map((o) => ({ o, sc: similar(o.name, nm) })).filter((x) => x.sc >= 0.6).sort((a, b2) => b2.sc - a.sc)[0]?.o;
    if (hit && !out.includes(hit.id)) out.push(hit.id);
  }
  return out;
}

/** Works SWI AI heard as done → ticks on the building's list (only lines that exist; new lines added as "own"). */
export function tickWorks(list: Work[], heard: string[]): Work[] {
  const out = list.map((w) => ({ ...w }));
  for (const h of heard) {
    const i = out.findIndex((w) => norm(w.text) === norm(h) || similar(w.text, h) >= 0.6);
    if (i >= 0) out[i]!.done = true;
    else out.push({ text: h, done: true, from: 'own' });
  }
  return out;
}

// ---------- month view ----------
export type MonthDay = { date: string; dow: number; log: Pick<DailyLog, 'status' | 'weather' | 'weather_stop_h' | 'barge' | 'hold_up'> | null; photos: number; holdUps: number; eot: number };
export function monthGrid(month: string, logs: (Pick<DailyLog, 'log_date' | 'status' | 'weather' | 'weather_stop_h' | 'barge' | 'hold_up' | 'transfers' | 'visitor_org_ids'> & { photos?: number; holdUps?: number; eot?: number })[], today: string) {
  const first = month + '-01';
  const days: MonthDay[] = [];
  for (let d = first; d.slice(0, 7) === month; d = addDays(d, 1)) {
    const l = logs.find((x) => x.log_date === d) ?? null;
    days.push({ date: d, dow: (new Date(d + 'T00:00:00Z').getUTCDay() + 6) % 7, log: l, photos: l?.photos ?? 0, holdUps: l?.holdUps ?? 0, eot: l?.eot ?? 0 });
  }
  const past = days.filter((d) => d.date <= today);
  const working = past.filter((d) => d.dow !== 6); // Sunday off
  const logged = working.filter((d) => d.log);
  const ls = logs.filter((l) => l.log_date.slice(0, 7) === month);
  return {
    days,
    summary: {
      working: working.length,
      logged: logged.length,
      missing: working.filter((d) => !d.log && d.date < today).map((d) => d.date),
      drafts: ls.filter((l) => l.status === 'draft').length,
      rainDays: ls.filter((l) => l.weather === 'showers' || l.weather === 'heavy_rain').length,
      hoursLost: Math.round(ls.reduce((t, l) => t + Number(l.weather_stop_h ?? 0), 0) * 10) / 10,
      bargeDays: ls.filter((l) => l.barge).length,
      transfers: ls.reduce((t, l) => t + Number(l.transfers ?? 0), 0),
      visits: ls.reduce((t, l) => t + (l.visitor_org_ids?.length ?? 0), 0),
      photos: ls.reduce((t, l) => t + (l.photos ?? 0), 0),
      holdUps: ls.reduce((t, l) => t + (l.holdUps ?? 0), 0),
      eot: ls.reduce((t, l) => t + (l.eot ?? 0), 0),
    },
  };
}
